import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Recommendation } from "../../api/types";
import { S } from "../../styles";
import { ConfidenceBadge } from "../../components/ConfidenceBadge";
import { ScoreBar } from "../../components/ScoreBar";
import { POStatusBanner } from "../purchase-orders/POStatusBanner";
import { TraceStatusPanel } from "../../components/TraceStatusPanel";
import { isTraceBlocking } from "../../api/types";

export function RecommendationCard({
  rec,
  partNumber,
  canAction,
  onAction,
  awardLocked = false,
  canOverrideTrace = false,
}: {
  rec: Recommendation;
  partNumber?: string | null;
  canAction: boolean;
  onAction: () => void;
  // True when the RFQ has already been awarded (a PO exists for one of its
  // quotes). Freezes the approve/reject/negotiate actions to match the backend
  // award-lock, which would otherwise reject these with a 409.
  awardLocked?: boolean;
  // True for manager/admin. Clearing a traceability block is an airworthiness
  // decision, so a buyer sees the block but cannot lift it — the API returns
  // 403 if they try. Defaults false so a caller that forgets it gets the safe
  // behaviour rather than the permissive one.
  canOverrideTrace?: boolean;
}) {
  const [feedback, setFeedback] = useState("");
  const [ackReview, setAckReview] = useState(false);
  const [ackTrace, setAckTrace] = useState(false);
  const awardLockTitle =
    "This RFQ has already been awarded — quotes are locked";

  const bestQuote = rec.scored_quotes.find(
    (q) => q.quote_id === rec.recommended_quote_id,
  );
  const statusColor: Record<string, string> = {
    pending_review: "#e67700",
    approved: "#2f9e44",
    auto_approved: "#1971c2",
    rejected: "#c92a2a",
    overridden: "#7c3aed",
  };

  const approveMutation = useMutation({
    mutationFn: () =>
      api.approveRecommendation(
        rec.id,
        feedback || undefined,
        ackReview,
        ackTrace,
      ),
    onSuccess: () => onAction(),
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.rejectRecommendation(rec.id, feedback || undefined),
    onSuccess: () => onAction(),
  });

  const negotiateMutation = useMutation({
    mutationFn: () =>
      api.startNegotiation(rec.rfq_id, rec.recommended_quote_id!),
    onSuccess: () => onAction(),
  });

  const busy =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    negotiateMutation.isPending;
  // Both gates disable Approve, and each is cleared by its own checkbox.
  const traceBlocked =
    isTraceBlocking(bestQuote?.trace_status) && !ackTrace;
  const approveBlocked =
    (!!bestQuote?.needs_review && !ackReview) || traceBlocked;
  const err =
    approveMutation.error ?? rejectMutation.error ?? negotiateMutation.error;
  const poResult = approveMutation.data ?? null;

  return (
    <div
      style={{
        ...S.card,
        borderLeft: `4px solid ${statusColor[rec.status] ?? "#ccc"}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 12,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#888",
              textTransform: "uppercase" as const,
              letterSpacing: 0.5,
            }}
          >
            RFQ Recommendation
          </span>
          <div style={{ fontSize: 13, color: "#555", marginTop: 2 }}>
            RFQ: {rec.rfq_id.slice(0, 8)}…
            {partNumber && (
              <span style={{ marginLeft: 10 }}>
                PN:{" "}
                <span style={{ fontWeight: 600, color: "#333" }}>
                  {partNumber}
                </span>
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <ConfidenceBadge score={rec.confidence} />
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 4,
              background: (statusColor[rec.status] ?? "#888") + "22",
              color: statusColor[rec.status] ?? "#888",
            }}
          >
            {rec.status.replace("_", " ").toUpperCase()}
          </span>
        </div>
      </div>

      {rec.summary && (
        <div
          style={{
            background: "#f8f9ff",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 12,
            fontWeight: 600,
            fontSize: 14,
            color: "#4f46e5",
          }}
        >
          🎯 {rec.summary}
        </div>
      )}

      {bestQuote && (
        <div style={{ ...S.card, background: "#f8fff9", marginBottom: 12 }}>
          <h3 style={{ ...S.h3, color: "#2f9e44" }}>
            Recommended Quote — {bestQuote.supplier_name}
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4px 16px",
              fontSize: 13,
            }}
          >
            <span style={{ color: "#666" }}>Unit Price</span>
            <span style={{ fontWeight: 700 }}>
              {bestQuote.currency} {bestQuote.unit_price.toFixed(2)}
            </span>
            <span style={{ color: "#666" }}>Lead Time</span>
            <span>
              {bestQuote.lead_time_days !== null
                ? `${bestQuote.lead_time_days} days`
                : "—"}
            </span>
            <span style={{ color: "#666" }}>Trace Docs</span>
            <span>
              {bestQuote.trace_status && bestQuote.trace_status !== "unassessed"
                ? bestQuote.trace_status.replace(/_/g, " ")
                : bestQuote.has_trace
                  ? "✓ Yes"
                  : "✗ No"}
            </span>
            {bestQuote.price_vs_baseline_pct !== null && (
              <>
                <span style={{ color: "#666" }}>vs. 90d baseline</span>
                <span
                  style={{
                    fontWeight: 600,
                    color:
                      bestQuote.price_vs_baseline_pct < 0
                        ? "#2f9e44"
                        : "#c92a2a",
                  }}
                >
                  {bestQuote.price_vs_baseline_pct > 0 ? "+" : ""}
                  {bestQuote.price_vs_baseline_pct.toFixed(1)}%
                </span>
              </>
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            <ScoreBar score={bestQuote.price_score} label="Price score" />
            <ScoreBar
              score={bestQuote.lead_time_score}
              label="Lead-time score"
            />
            <ScoreBar score={bestQuote.trace_score} label="Trace docs" />
            <ScoreBar score={bestQuote.overall_score} label="Overall" />
          </div>
        </div>
      )}

      {rec.reasoning && (
        <details style={{ marginBottom: 12 }}>
          <summary
            style={{
              cursor: "pointer",
              fontSize: 13,
              color: "#4f46e5",
              fontWeight: 500,
            }}
          >
            🔍 Full AI reasoning
          </summary>
          <p
            style={{
              ...S.muted,
              marginTop: 8,
              fontSize: 13,
              lineHeight: 1.5,
              fontStyle: "italic",
            }}
          >
            {rec.reasoning}
          </p>
        </details>
      )}

      {rec.scored_quotes.length > 1 && (
        <details style={{ marginBottom: 12 }}>
          <summary
            style={{
              cursor: "pointer",
              fontSize: 13,
              color: "#555",
              fontWeight: 500,
            }}
          >
            📊 All {rec.scored_quotes.length} scored quotes
          </summary>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column" as const,
              gap: 6,
            }}
          >
            {[...rec.scored_quotes]
              .sort((a, b) => b.overall_score - a.overall_score)
              .map((sq, i) => (
                <div
                  key={sq.quote_id}
                  style={{
                    padding: "8px 12px",
                    background:
                      sq.quote_id === rec.recommended_quote_id
                        ? "#f0f4ff"
                        : "#fafafa",
                    borderRadius: 6,
                    border: `1px solid ${sq.quote_id === rec.recommended_quote_id ? "#4f46e544" : "#e8eaf0"}`,
                    fontSize: 12,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    #{i + 1} {sq.supplier_name}{" "}
                    {sq.quote_id === rec.recommended_quote_id && (
                      <span style={{ color: "#4f46e5" }}>★ Recommended</span>
                    )}
                  </div>
                  <div style={{ color: "#666" }}>
                    {sq.currency} {sq.unit_price.toFixed(2)} ·{" "}
                    {sq.lead_time_days !== null
                      ? `${sq.lead_time_days}d`
                      : "lead ?"}
                    · trace: {sq.has_trace ? "✓" : "✗"} · overall:{" "}
                    {Math.round(sq.overall_score * 100)}%
                  </div>
                  {sq.analysis && (
                    <div style={{ color: "#888", marginTop: 2 }}>
                      {sq.analysis}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </details>
      )}

      {rec.status === "pending_review" && canAction && (
        <div
          style={{
            borderTop: "1px solid #e8eaf0",
            paddingTop: 12,
            marginTop: 4,
          }}
        >
          <label style={S.label}>
            Feedback (optional — logged for AI training)
          </label>
          <input
            style={{ ...S.input, marginBottom: 10 }}
            placeholder="Reason for approval or rejection…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          {bestQuote?.needs_review && (
            // The API refuses to raise a PO for a flagged quote unless this is
            // ticked, so without the control the buyer would meet a 409 they
            // could do nothing about.
            <div
              role="alert"
              style={{
                background: "#fff4e6",
                border: "1px solid #ffd8a8",
                borderRadius: 4,
                padding: "8px 10px",
                margin: "0 0 10px",
                fontSize: 13,
                color: "#8a5a00",
              }}
            >
              <strong>⚠ This quote needs review</strong>
              {bestQuote.review_reason ? <> — {bestQuote.review_reason}</> : null}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 8,
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={ackReview}
                  onChange={(e) => setAckReview(e.target.checked)}
                />
                I have checked this and want to order it anyway
              </label>
            </div>
          )}
          <TraceStatusPanel
            status={bestQuote?.trace_status}
            summary={bestQuote?.trace_summary}
            canOverride={canOverrideTrace}
            canAct={!awardLocked}
            acknowledged={ackTrace}
            onAcknowledge={setAckTrace}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
            <button
              style={{
                ...S.btn,
                background: "#2f9e44",
                cursor:
                  awardLocked || approveBlocked ? "not-allowed" : undefined,
                opacity: busy || awardLocked || approveBlocked ? 0.5 : 1,
              }}
              onClick={() => approveMutation.mutate()}
              disabled={busy || awardLocked || approveBlocked}
              title={
                awardLocked
                  ? awardLockTitle
                  : bestQuote?.needs_review && !ackReview
                    ? "Acknowledge the review flag above before approving"
                    : traceBlocked
                      ? canOverrideTrace
                        ? "Accept the traceability shortfall above before approving"
                        : "A manager must accept the traceability shortfall above"
                      : undefined
              }
            >
              ✓ Approve
            </button>
            <button
              style={{
                ...S.btn,
                background: "#c92a2a",
                cursor: awardLocked ? "not-allowed" : undefined,
                opacity: busy || awardLocked ? 0.5 : 1,
              }}
              onClick={() => rejectMutation.mutate()}
              disabled={busy || awardLocked}
              title={awardLocked ? awardLockTitle : undefined}
            >
              ✗ Reject
            </button>
            {rec.recommended_quote_id && (
              <button
                style={{
                  ...S.btn,
                  background: "#7c3aed",
                  cursor: awardLocked ? "not-allowed" : undefined,
                  opacity:
                    busy || awardLocked || negotiateMutation.isSuccess
                      ? 0.5
                      : 1,
                }}
                onClick={() => negotiateMutation.mutate()}
                disabled={busy || awardLocked || negotiateMutation.isSuccess}
                title={
                  awardLocked
                    ? awardLockTitle
                    : "Launch AI negotiation agent against the recommended quote"
                }
              >
                🤝{" "}
                {negotiateMutation.isSuccess
                  ? "Negotiation Started"
                  : "Negotiate"}
              </button>
            )}
          </div>
          {awardLocked && (
            <p
              style={{
                ...S.muted,
                marginTop: 8,
                marginBottom: 0,
                fontSize: 12,
              }}
            >
              🔒 RFQ already awarded — this recommendation can no longer be
              actioned.
            </p>
          )}
          {err && <p style={S.err}>{err.message}</p>}
        </div>
      )}

      {rec.status === "auto_approved" && (
        <div
          style={{
            borderTop: "1px solid #e8eaf0",
            paddingTop: 12,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 4,
              background: "#1971c222",
              color: "#1971c2",
            }}
          >
            🤖 Auto-Ordered
          </span>
          <span style={{ ...S.muted, fontSize: 12 }}>
            Ordered autonomously by policy
            {rec.actioned_at &&
              ` · ${new Date(rec.actioned_at).toLocaleString()}`}
          </span>
        </div>
      )}

      {rec.status !== "pending_review" &&
        rec.status !== "auto_approved" &&
        rec.actioned_at && (
          <div style={{ ...S.muted, fontSize: 12, marginTop: 8 }}>
            {rec.status} by user {rec.actioned_by_user_id?.slice(0, 8)}… at{" "}
            {new Date(rec.actioned_at).toLocaleString()}
            {rec.feedback && ` — "${rec.feedback}"`}
          </div>
        )}

      {poResult && (
        <POStatusBanner
          poNumber={poResult.po_number}
          poStatus={poResult.po_status}
          needsManager={poResult.po_needs_manager_approval}
        />
      )}
    </div>
  );
}
